import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import invoiceItemsTemplate from './invoiceItems.template.html';

export default angular
  .module('books.ui.invoiceItems', [csmCommonWeb])
  .factory('DGInvoiceItem', DGInvoiceItemFactory)
  .directive('invoiceItems', invoiceItemsDirective).name;

/*****************************************************************************
 * Constructor Factory: DGInvoiceItem
 *
 * Returns a constructor function to create objects to manage invoice items
 *****************************************************************************/
/* @ngInject */
function DGInvoiceItemFactory(Tools) {
  /*****************************************************************************
   * Class: DGInvoiceItem
   *
   * Datagrid invoice item class. This will manage a child BillItem object.
   *
   * @param src         Source object to populate properties
   *****************************************************************************/
  function DGInvoiceItem(src) {
    /** Internal BillItem to manage. */
    var mBillItem = src || {};

    /** Current Tax Rate as %. */
    var mTaxRate = 0;

    /*------------*/
    /* Item Types */
    /*------------*/
    Object.defineProperty(this, 'isProduct', {
      get: function () {
        return mBillItem.productID > 0;
      }
    });
    Object.defineProperty(this, 'isService', {
      get: function () {
        return !this.isProduct && !this.isComment;
      }
    });
    Object.defineProperty(this, 'isComment', {
      get: function () {
        return !this.isProduct && !mBillItem.accountID;
      }
    });

    /***************************************************************************
     * Display Properties
     ***************************************************************************/
    /** Accessor for the accountID field. */
    Object.defineProperty(this, 'accountID', {
      get: function () {
        return mBillItem.accountID;
      },
      set: function (value) {
        mBillItem.accountID = value;
      }
    });

    /** Accessor for the cost field. */
    Object.defineProperty(this, 'cost', {
      get: function () {
        return mBillItem.cost;
      },
      set: function (value) {
        if (value < 0) return;

        mBillItem.cost = value;
        recalc();
      }
    });

    /** Accessor for the freight field. */
    Object.defineProperty(this, 'freight', {
      get: function () {
        return mBillItem.freight;
      },
      set: function (value) {
        if (isNaN(value) || value < 0) return;

        mBillItem.freight = Tools.roundToPrecision(value || 0, 2);
        recalc();
      }
    });

    /** Accessor for the item/memo field. Setter can be text or an Product item */
    Object.defineProperty(this, 'memo', {
      get: function () {
        return mBillItem.memo;
      },
      set: function (value) {
        var forProduct = angular.isObject(value);
        mBillItem.memo = forProduct ? value.name : value;
        mBillItem.productID = forProduct ? value.id : NaN;
      }
    });

    /** Accessor for the qty field. */
    Object.defineProperty(this, 'qty', {
      get: function () {
        return mBillItem.quantity;
      },
      set: function (value) {
        if (isNaN(value) || value < 0) return;

        mBillItem.quantity = Math.ceil(Number(value));
        recalc();
      }
    });

    /** Accessor for the subtotal field. */
    Object.defineProperty(this, 'subtotal', {
      get: function () {
        return mBillItem.subTotal;
      }
    });

    /** Accessor for the taxAmount field. */
    Object.defineProperty(this, 'taxAmount', {
      get: function () {
        return mBillItem.tax;
      },
      set: function (value) {
        if (isNaN(value) || value < 0) return;

        mBillItem.tax = Tools.roundToPrecision(value, 2);
        mTaxRate = mBillItem.subTotal
          ? (mBillItem.tax / mBillItem.subTotal) * 100
          : 0;
        recalc();
      }
    });

    /** Accessor for the taxRate field. */
    Object.defineProperty(this, 'taxRate', {
      get: function () {
        return mTaxRate;
      },
      set: function (value) {
        if (value < 0) return;

        mTaxRate = Tools.roundToPrecision(value || 0, 2);
        recalc();
      }
    });

    /** Accessor for the total field. */
    Object.defineProperty(this, 'total', {
      get: function () {
        return mBillItem.total;
      },
      set: function (value) {
        if (!mBillItem.isService && value < 0) return;

        mBillItem.total = Tools.roundToPrecision(value, 2);
      }
    });

    Object.defineProperty(this, 'amountOwed', {
      get: function () {
        return mBillItem.amountOwed;
      },
    });

    /** Accessor for source BillItem. */
    this.getSource = function () {
      return mBillItem;
    };

    /***************************************************************************
     * recalc */
    /**
     * Update total properties on the source item.
     ***************************************************************************/
    function recalc() {
      /*--------------------------------------------------------*/
      /* Calculate the total only if this has a quantity, as an */
      /* item without a quantity has the total set by the user. */
      /*--------------------------------------------------------*/
      if (mBillItem.quantity > 0) {
        mBillItem.subTotal = Tools.roundToPrecision(
          mBillItem.quantity * mBillItem.cost,
          2
        );
        mBillItem.tax = Tools.roundToPrecision(
          (mTaxRate / 100) * mBillItem.subTotal,
          2
        );
        mBillItem.total = Tools.roundToPrecision(
          mBillItem.subTotal + mBillItem.tax + (mBillItem.freight || 0),
          2
        );
      } else
        mBillItem.quantity =
          mBillItem.cost =
          mBillItem.tax =
          mBillItem.subTotal =
          mBillItem.freight =
            NaN;
    }

    /*--------------------*/
    /* Initialize taxRate */
    /*--------------------*/
    this.taxAmount = mBillItem.tax;

    recalc();
  }

  return DGInvoiceItem;
}

/*****************************************************************************
 * Directive: invoiceItems
 *
 * Directive to display options for adding invoices to a grid. Includes a memo
 * selector, account selector and quantity and pricing details. To be used with
 * the invoiceVendor directive.
 *
 * @attr {object}     [invoice]    Object to hold the items for the invoice.
 * @attr {expression} [reviewOnly] Flag indicating that the current invoice can
 *                    not be modified. This will be true for paid invoices.
 * @attr {expression} [itemsReferenced] Flag indicating that the invoice contains
 *                    items that have been referenced.
 * @attr {expression} [hideItems]  Indication to hide the items datagrid.
 *****************************************************************************/
/* @ngInject */
function invoiceItemsDirective(
  $q,
  CommonUIGridTemplates,
  BooksSvc,
  Alert,
  BusyScreen,
  DGInvoiceItem,
  InventoryProducts,
  KEYCODES,
  uiGridConstants
) {
  /*----------------------*/
  /* Add block style rule */
  /*----------------------*/
  var mBody = angular.element(document.querySelector('body'));
  mBody.append('<style>invoice-items { display: block} </style>');

  /** Directive Controller. */
  var invoiceItemsController = function ($timeout, $scope, Tools) {
    /*----------------*/
    /* Data Providers */
    /*----------------*/
    /** Accounts. */ $scope.dpAccounts = [];
    /** Products. */ $scope.dpProducts = [];

    /*-------------------------*/
    /* Invoice item validation */
    /*-------------------------*/
    $scope.valItem = {};

    /*---------------------------------------*/
    /* checkbox value to allow for tax input */
    /*---------------------------------------*/
    $scope.allowTax = false;

    /***************************************************************************
     * accountLabel */
    /**
     * Returns the account name for the corresponding accountID if available. The
     * function should be defined before being used in the ui-grid.
     *
     * @param   input   Account ID or DGInvoiceItem
     * @return  String  Label to be used for accounts in typeahead
     ***************************************************************************/
    $scope.accountLabel = function (input) {
      var acctID = angular.isObject(input) ? input.accountID : Number(input);
      return (
        $scope.dpAccounts
          .filter(function (a) {
            return a.accountID == acctID;
          })
          .shift() || {}
      ).accountName;
    };

    /*-------------------------------------*/
    /* Editable cell template for the      */
    /* Account field in the Invoices Grid. */
    /*-------------------------------------*/
    var invcAcctCellTemplate =
      '<div style="display:block; height:100%; z-index:1;"> ' +
      '<form name="inputForm" style="display:block; height:100%;">' +
      '<input type="INPUT_TYPE" ng-class="\'colt\' + col.uid" ui-grid-edit-typeahead ng-model="row.entity.accountID" ' +
      ' uib-typeahead="acct.accountID as acct.accountName for acct in grid.appScope.dpAccounts | filter:{ accountName: $viewValue } | limitTo:8" ' +
      ' typeahead-select-on-blur="true" typeahead-input-formatter="grid.appScope.accountLabel($model)" style="display:block; height:100%;" />' +
      '</form>' +
      '</div>';

    /** Delete column definition. */
    var mDeleteCol = {
      field: 'delete',
      maxWidth: 10,
      displayName: '',
      cellTemplate: CommonUIGridTemplates.CELL_DELETE_ROW,
      appScope: $scope
    };

    var columnDefs = [
      {
        field: 'accountID',
        displayName: 'Account',
        editableCellTemplate: invcAcctCellTemplate,
        enableCellEdit: true,
        cellEditableCondition: allowEditAccount,
        labelFunction: $scope.accountLabel
      },
      {
        field: 'memo',
        displayName: 'Item',
        editableCellTemplate: CommonUIGridTemplates.CELL_DEFAULT,
        enableCellEdit: true,
        cellEditableCondition: allowEditMemo
      },
      {
        field: 'qty',
        maxWidth: 50,
        editableCellTemplate: CommonUIGridTemplates.CELL_DEFAULT,
        cellFilter: 'number',
        enableCellEdit: true,
        cellEditableCondition: allowEditQty
      },
      {
        field: 'cost',
        maxWidth: 80,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 2,
        cellFilter: 'currency',
        enableCellEdit: true,
        cellEditableCondition: allowEditPrice
      },
      {
        field: 'subtotal',
        maxWidth: 90,
        cellFilter: 'currency'
      },
      {
        field: 'taxAmount',
        maxWidth: 80,
        displayName: 'Tax',
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 2,
        cellFilter: 'currency',
        enableCellEdit: true,
        cellEditableCondition: allowEditTax
      },
      {
        field: 'freight',
        maxWidth: 80,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 2,
        cellFilter: 'currency',
        enableCellEdit: true,
        cellEditableCondition: allowEditFreight
      },
      {
        field: 'total',
        maxWidth: 90,
        editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
        precision: 2,
        cellFilter: 'currency',
        enableCellEdit: true,
        cellEditableCondition: allowEditAmount
      },
      {
        field: 'amountOwed',
        displayName: 'Amount Owed',
        maxWidth: 100,
        cellFilter: 'currency',
        type: 'number',
        enableCellEdit: false
      },
      mDeleteCol
    ];

    $scope.invoicesGrid = {
      columnDefs: columnDefs,
      enableCellEdit: false,
      enableCellEditOnFocus: true,
      enableColumnMenus: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      minRowsToShow: 5,
      onRegisterApi: function (gridApi) {
        $scope.invoicesGrid.api = gridApi;
      },
      data: []
    };

    /*------------------*/
    /* Allow edit flags */
    /*------------------*/
    function allowEditAmount(gridScope) {
      return (
        !$scope.reviewOnly() &&
        Tools.isNaNZero(gridScope.row.entity.qty) &&
        !gridScope.row.entity.isComment
      );
    }
    function allowEditAccount(gridScope) {
      return !$scope.reviewOnly() && gridScope.row.entity.total > 0;
    }
    function allowEditFreight(gridScope) {
      return !$scope.reviewOnly() && gridScope.row.entity.qty > 0;
    }
    function allowEditMemo(gridScope) {
      return (
        !$scope.reviewOnly() &&
        Tools.isNaNZero(gridScope.row.entity.getSource().productID)
      );
    }
    function allowEditPrice(gridScope) {
      return !$scope.reviewOnly() && !Tools.isNaNZero(gridScope.row.entity.qty);
    }
    function allowEditQty(gridScope) {
      return !$scope.reviewOnly() && gridScope.row.entity.qty > 0;
    }
    function allowEditTax(gridScope) {
      return !$scope.reviewOnly() && gridScope.row.entity.qty > 0;
    }

    /***************************************************************************
     * addInvcItem */
    /**
     * 'Add Item' button click handler. Validates the invoice item and adds it
     * to the grid.
     ***************************************************************************/
    $scope.addInvcItem = function () {
      $scope.valItem.reset();

      /* Abort save if validation fails */
      if (!$scope.valItem.validate()) return;

      /*-------------------------------------------*/
      /* Entry is to be treated as just a comment. */
      /*-------------------------------------------*/
      if ($scope.invcItem.isComment) {
        var comment = $scope.invcItem.memo;
        $scope.invcItem = new DGInvoiceItem();
        $scope.invcItem.memo = comment;

        $scope.invoicesGrid.data.push($scope.invcItem);
        $scope.clearInput();
        $scope.allowTax = false;
      } else if ($scope.invcItem.isService) {
        /*----------------------------------*/
        /* User has entered a new free form */
        /* item with a cost and a quantity. */
        /*----------------------------------*/
        if (!$scope.invcItem.cost && !$scope.invcItem.qty) {
          $scope.invcItem.cost = NaN;
          $scope.invcItem.freight = $scope.invcItem.freight || 0;
          $scope.invcItem.qty = NaN;
        }

        /*--------------------------------------------------*/
        /* Use tax rate if tax is checked. Service charges  */
        /* with tax checked will be freeform item types     */
        /* (items that are not available in the items list) */
        /*--------------------------------------------------*/
        if (!$scope.allowTax) $scope.invcItem.taxRate = NaN;

        $scope.invoicesGrid.data.push($scope.invcItem);
        $scope.clearInput();
        $scope.allowTax = false;
      } else if ($scope.invcItem.isProduct) {
        /*---------------------------------------------------*/
        /* Entry is a product. Call service, display results */
        /*---------------------------------------------------*/
        /*----------------------------------------*/
        /* Lookup product that matches product ID */
        /*----------------------------------------*/
        var product = $scope.dpProducts
          .filter(function (prod) {
            return prod.id == $scope.invcItem.getSource().productID;
          })
          .shift();

        /* Verify the item has multiple selection options available  */
        InventoryProducts.verify(product).then(function (resp) {
          /* If multiple options returned, map each to a DGInvoiceItem */
          if (resp.multi) {
            resp.items.forEach(function (ppItem) {
              var billItem = new DGInvoiceItem();
              billItem.qty = Number(ppItem.qty);
              billItem.cost = Number(ppItem.cost);
              billItem.freight = Number(ppItem.freight);
              billItem.itemID = Number(ppItem.itemID);
              billItem.inventoryID = Number(ppItem.inventoryProductID);
              billItem.taxRate = Number(ppItem.getSource().taxRate);
              billItem.memo = {
                name: ppItem.memo,
                id: ppItem.getSource().itemID
              };

              /*------------------------------------------------*/
              /* If account has been entered, add on each item. */
              /*------------------------------------------------*/
              billItem.accountID = $scope.invcItem.accountID || null;

              $scope.invoicesGrid.data.push(billItem);
            });
          } else {
            $scope.invcItem.getSource().inventoryProductID = resp.items.id;
            $scope.invoicesGrid.data.push($scope.invcItem);
          }
          $scope.clearInput();
          $scope.allowTax = false;
        });
      }
    };

    /***************************************************************************
     * checkItem */
    /**
     * Checks if there is an item selection. If there is, set the cost and initial
     * quantity. Otherwise, clear the product related fields.
     ***************************************************************************/
    $scope.checkItem = function () {
      var mProduct = $scope.dpProducts
        .filter(function (prod) {
          return prod.id == $scope.invcItem.getSource().productID;
        })
        .shift();

      /*----------------------------------------------------*/
      /* No product selected, clear item details. Retain    */
      /* memo in the event that a comment has been entered. */
      /*----------------------------------------------------*/
      if (!mProduct) {
        var memo = $scope.invcItem.memo;
        $scope.invcItem = new DGInvoiceItem();
        $scope.invcItem.memo = memo;
      } else {
        /*------------------------------------------------------*/
        /* A product has been selected. Pre-populate the inputs */
        /*------------------------------------------------------*/
        $scope.invcItem.qty = 1;
        $scope.invcItem.cost = mProduct.averageCost;
        $scope.invcItem.freight = 0;

        /*-----------------------------------------------------------*/
        /* If the product is tracked in inventory, clear the account */
        /*-----------------------------------------------------------*/
        if (mProduct.trackInventory) {
          $scope.invcItem.accountID = NaN;
          // This will disable the account selector if the product is being tracked in inventory
          // it gets reset in $scope.clearInput.
          $scope.invcItem.trackInventory = mProduct.trackInventory;
        }
      }
    };

    /***************************************************************************
     * clearInput */
    /**
     * Clears all input fields.
     ***************************************************************************/
    $scope.clearInput = function () {
      if ($scope.valItem.reset) $scope.valItem.reset();
      $scope.invcItem = new DGInvoiceItem();
      $scope.invcItem.trackInventory = false;
    };

    /***************************************************************************
     * disableValidator */
    /**
     * Returns a boolean indicating if the validator should be enabled or disabled.
     *
     * @return  Boolean   True if invoice items have been entered into the grid,
     *                    false not.
     ***************************************************************************/
    $scope.disableValidator = function () {
      return $scope.invoice.items ? $scope.invoice.items.length > 0 : false;
    };

    /***************************************************************************
     * formatAcctLabel */
    /**
     * For formatting the typeahed ng-model result after selection in the invoices
     * grid. Returns the account name if available
     *
     * @param   acct    Account object
     * @return  String  Account Name
     ***************************************************************************/
    $scope.formatAcctLabel = function (acct) {
      return acct ? acct : '';
    };

    /***************************************************************************
     * init */
    /**
     * Setup the grid when the directive is loaded.
     ***************************************************************************/
    $scope.init = function () {
      $scope.invoicesGrid.columnDefs = columnDefs;
      $scope.clearInput();
    };

    /***************************************************************************
     * synchronizeItems */
    /**
     * Keeps the items visible in the ui-grid in sync with the items array
     * whenever the grid changes.
     ***************************************************************************/
    function synchronizeItems() {
      var invItems = $scope.invoice
        ? $scope.invoice.items || ($scope.invoice.items = [])
        : [];
      var dgItems = $scope.invoicesGrid.data;

      /*---------------------------------------------------*/
      /* Remove items from invoice that aren't in the grid */
      /*---------------------------------------------------*/
      for (var i = invItems.length - 1; i > -1; i--)
        if (
          !dgItems.some(function (dgi) {
            return dgi.getSource() == invItems[i];
          })
        )
          invItems.splice(i, 1);

      /*--------------------------*/
      /* Add new items to invoice */
      /*--------------------------*/
      dgItems.forEach(function (dgi) {
        if (
          !invItems.some(function (item) {
            return item == dgi.getSource();
          })
        )
          invItems.push(dgi.getSource());
      });
    }

    /***************************************************************************
     * total */
    /**
     * Returns the grand total of all invoices currently in the grid.
     *
     * @param   gridScope  ui-grid scope
     * @return  String     Invoices total
     ***************************************************************************/
    $scope.total = function () {
      return ($scope.invoicesGrid.data || []).reduce(function (sum, item) {
        return Number(item['total']) ? sum + Number(item['total']) : sum;
      }, 0);
    };

    $scope.totalPaid = () => ($scope.invoicesGrid.data || []).reduce((sum, invoiceItem) => {
      const amountPaid = Number(invoiceItem['total']) - Number(invoiceItem['amountOwed']);
      return amountPaid > 0 ? sum + amountPaid : sum;
    }, 0);

    /***************************************************************************
     * validTotal */
    /**
     * Returns true the current invoice has a total greater than 0
     *
     * @return  Boolean    Indication that the total is valid.
     ***************************************************************************/
    $scope.validTotal = function () {
      return Number($scope.invcItem.total) > 0;
    };

    /*--------------------------------------------------------*/
    /* Set up listener for the delete row click event request */
    /*--------------------------------------------------------*/
    $scope.$on(
      CommonUIGridTemplates.EVENT_DELETE_ROW,
      function (event, grid, row) {
        /*------------------------------------------------------*/
        /* Prevent deletion of item when delete icon is hidden. */
        /*------------------------------------------------------*/
        if (row.entity.hideDelete && row.entity.hideDelete()) return;

        BusyScreen.setBusy(true);

        $q.when(
          row.entity.getSource().id
            ? BooksSvc.billItemReferencedByCheck(
                row.entity.getSource().id
              ).then(function (resp) {
                if (resp.data) return $q.reject();
              })
            : null
        )
          .then(
            /** Resolved means new item or not being referenced. */
            function () {
              /*------------------*/
              /* Remove the item. */
              /*------------------*/
              var index = grid.options.data.indexOf(row.entity);
              grid.options.data.splice(index, 1);

              /*------------------*/
              /* Refresh the grid */
              /*------------------*/
              grid.options.api.core.refresh();
            },
            /** Reject means existing item is referenced by check. */
            function () {
              Alert.error('Item referenced by a check.', 'Unable to Delete');
            }
          )
          ['finally'](function () {
            BusyScreen.setBusy(false);
          });
      }
    );

    /*-----------------------------------------------*/
    /* Watch for invoice changes to reset validators */
    /*-----------------------------------------------*/
    $scope.$watch('invoice', function () {
      if ($scope.valItem.reset) $scope.valItem.reset();
    });

    /*-------------------------------------------------------------*/
    /* Watch for invoice changes to update the data list reference */
    /*-------------------------------------------------------------*/
    $scope.$watch('invoice.items', function (itemList) {
      $scope.invoicesGrid.data = (itemList || []).map(function (item) {
        return new DGInvoiceItem(item);
      });

      /*-------------------------------------------------------*/
      /* Add the hideDelete function on each row entity that   */
      /* will be read by the delete row template. On invoices  */
      /* with items that have been referenced, this will allow */
      /* for the hiding of the delete icon on those items.     */
      /*-------------------------------------------------------*/
      if ($scope.itemsReferenced())
        angular.forEach($scope.invoicesGrid.data, function (dgi) {
          dgi.hideDelete = function () {
            return dgi.total - dgi.getSource().amountOwed !== 0;
          };
        });

      /*-------------------------------------------------------------------*/
      /* Clear any previously selected grid cells when the items reference */
      /* changes (using undocumented ui-grid cellNav functionality)        */
      /*-------------------------------------------------------------------*/
      if ($scope.invoicesGrid.api) {
        $scope.invoicesGrid.api.grid.cellNav.clearFocus();
        $scope.invoicesGrid.api.grid.cellNav.lastRowCol = null;
      }
    });

    /*---------------------------------------------*/
    /* For for changes in the dg collection so the */
    /* invoice items can be updated accordingly    */
    /*---------------------------------------------*/
    $scope.$watchCollection('invoicesGrid.data', synchronizeItems);

    /*-------------------------------------------------------*/
    /* Watch for when the reviewOnly expression changes so   */
    /* the delete column can be updated accordingly. The     */
    /* column is hidden on invoices that cannot be modified. */
    /*-------------------------------------------------------*/
    $scope.$watch(
      function () {
        return $scope.reviewOnly();
      },
      function (value) {
        mDeleteCol.visible = !value;
      }
    );

    /*----------------*/
    /* Initialize app */
    /*----------------*/
    $scope.init();

    /*-------------------*/
    /* Load the accounts */
    /*-------------------*/
    BooksSvc.viewChartOfAccounts('Expense,Trust,Other Liability', true).then(
      function (resp) {
        $scope.dpAccounts = resp.data;
        BooksSvc.getProductList(true, false, false, false).then(function (
          resp
        ) {
          $scope.dpProducts = resp.data.sort(function (a, b) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          });
        });
      }
    );
  };

  return {
    controller: invoiceItemsController,
    restrict: 'E',
    scope: {
      invoice: '=',
      reviewOnly: '&',
      itemsReferenced: '&',
      hideItems: '&'
    },
    template: invoiceItemsTemplate
  };
}
